/* Cimafer · guardião da Sacola (persistência independente de login)
   O carrinho da Shopify vive num cookie de sessão (~14 dias) e some quando o
   cookie expira/cai — independente do cliente estar logado. Este script guarda
   um backup dos itens em localStorage (mesmo padrão da wishlist) e, quando a
   sacola do servidor amanhece vazia com backup válido, re-adiciona os itens.
   NÃO restaura depois de compra concluída: ao ir pro checkout marcamos uma
   flag; se a sacola voltar vazia com a flag ativa, o backup é descartado
   (a compra é a explicação provável — melhor perder o backup que duplicar). */
(() => {
  const KEY = 'cima:cart-backup';       // {items:[{id,quantity,properties?}], token, ts}
  const PENDING = 'cima:cart-checkout'; // {ts} — cliente seguiu pro checkout
  const GUARD = 'cima:cart-restored';   // sessionStorage: 1 tentativa por sessão
  const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 dias

  const readJSON = (store, k) => {
    try { return JSON.parse(store.getItem(k)); } catch (e) { return null; }
  };
  const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* noop */ } };
  const drop = (k) => { try { localStorage.removeItem(k); } catch (e) { /* noop */ } };

  const getCart = () =>
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const saveBackup = (cart) => {
    const items = (cart.items || []).map((l) => {
      const it = { id: l.id, quantity: l.quantity };
      if (l.handle) it.handle = l.handle; // p/ limpar a lista de desejos pós-compra
      if (l.properties && Object.keys(l.properties).length) it.properties = l.properties;
      return it;
    });
    if (items.length) write(KEY, { items, token: cart.token, ts: Date.now() });
    else drop(KEY);
  };

  // Re-adiciona os itens do backup. Bulk primeiro; se algum item não existe
  // mais / sem estoque a Shopify recusa o lote inteiro (422) → um a um,
  // ignorando os que falharem. `cima_restore=1` marca a chamada pro
  // cima-datalayer NÃO contar como add_to_cart do cliente.
  const restore = async (items) => {
    const post = (body) =>
      fetch('/cart/add.js?cima_restore=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
    try {
      const bulk = await post({ items });
      if (!bulk.ok) {
        for (const it of items) {
          try { await post({ items: [it] }); } catch (e) { /* segue */ }
        }
      }
      const cart = await getCart();
      if (cart.item_count > 0) { saveBackup(cart); return true; }
    } catch (e) { /* rede: fica pra próxima visita */ }
    return false;
  };

  // Compra concluída: itens comprados saem da lista de desejos (cima:wishlist,
  // array de handles — mesmo contrato do header/favoritos, que escutam o evento).
  const removeFromWishlist = (items) => {
    try {
      const handles = (items || []).map((i) => String(i.handle || '')).filter(Boolean);
      if (!handles.length) return;
      const raw = JSON.parse(localStorage.getItem('cima:wishlist') || '[]');
      if (!Array.isArray(raw)) return;
      const next = raw.map(String).filter((h) => handles.indexOf(h) === -1);
      if (next.length === raw.length) return;
      localStorage.setItem('cima:wishlist', JSON.stringify(next));
      document.dispatchEvent(new CustomEvent('cima:wishlist:change'));
    } catch (e) { /* noop */ }
  };

  const sync = async () => {
    let cart;
    try { cart = await getCart(); } catch (e) { return; }
    if (cart.item_count > 0) {
      saveBackup(cart);
      drop(PENDING); // voltou do checkout sem comprar (sacola segue viva)
      return;
    }
    const bk = readJSON(localStorage, KEY);
    if (!bk || !bk.items || !bk.items.length) return;
    const pend = readJSON(localStorage, PENDING);
    if (pend) {
      if (Date.now() - (pend.ts || 0) < 7 * 24 * 60 * 60 * 1000) removeFromWishlist(bk.items);
      drop(KEY); drop(PENDING); return;
    }
    if (Date.now() - (bk.ts || 0) > MAX_AGE_MS) { drop(KEY); return; }
    try {
      if (sessionStorage.getItem(GUARD)) return;
      sessionStorage.setItem(GUARD, '1');
    } catch (e) { return; }
    if (await restore(bk.items)) location.reload(); // re-renderiza badge/drawer/carrinho
  };

  // Mantém o backup fresco a cada mudança feita pelo tema (drawer, PDP, /cart).
  // Sacola esvaziada PELO CLIENTE (remove/clear) → backup limpo junto.
  let t = null;
  const scheduleSync = () => {
    clearTimeout(t);
    t = setTimeout(() => {
      getCart().then(saveBackup).catch(() => {});
    }, 400);
  };
  const _fetch = window.fetch;
  window.fetch = function () {
    const input = arguments[0];
    const url = (input && input.url) ? input.url : (typeof input === 'string' ? input : '');
    const p = _fetch.apply(this, arguments);
    if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
      p.then((res) => { if (res && res.ok) scheduleSync(); }).catch(() => {});
    }
    return p;
  };

  // Intenção de checkout: qualquer clique/submit que leve pro checkout.
  document.addEventListener('click', (ev) => {
    const el = ev.target && ev.target.closest
      ? ev.target.closest('a[href*="/checkout"], button[name="checkout"], [data-cima-checkout]')
      : null;
    if (el) write(PENDING, { ts: Date.now() });
  }, true);
  document.addEventListener('submit', (ev) => {
    const f = ev.target;
    if (f && typeof f.action === 'string' && f.action.indexOf('/checkout') !== -1) {
      write(PENDING, { ts: Date.now() });
    }
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
})();
